import * as React from 'react';

import { cn } from '@/lib/utils';
import { TitleDescriptionInfo } from '@/components/ui/title-description-info';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Render as a frosted glass panel instead of a solid card. Use for surfaces
   * that float over page content (sticky headers, floating bars) rather than
   * for cards sitting in the page flow.
   */
  glass?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, glass = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border-0 text-card-foreground',
        glass ? 'glass-surface glass-subtle' : 'bg-card shadow-card',
        className
      )}
      {...props}
    />
  )
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-4 md:p-6', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

export interface CardTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Shown in a tooltip on the info icon beside the title */
  description?: React.ReactNode;
  infoAriaLabel?: string;
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, children, description, infoAriaLabel, ...props }, ref) => (
    <div className="flex min-w-0 items-center gap-1.5">
      <h3
        ref={ref}
        className={cn(
          'card-title-text min-w-0 font-semibold leading-none tracking-tight',
          className
        )}
        {...props}
      >
        {children}
      </h3>
      {description ? (
        <TitleDescriptionInfo
          description={description}
          infoAriaLabel={infoAriaLabel}
        />
      ) : null}
    </div>
  )
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('p-4 pt-0 md:p-6 md:pt-0', className)}
    {...props}
  />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-4 pt-0 md:p-6 md:pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
